import { forEach, isEmpty, keyBy, map } from 'lodash';
import { MutableRefObject, useEffect, useRef } from 'react';
import { SimpleMemoryCache } from '../../common/caches/simple-memory-cache';
import { FeatureLayer, Graphic, GraphicsLayer } from '../constructors';
import type { FeatureLayerApplyEdits, MapMode } from '../types';
import { getFeatureEntityIds, logMapInfo } from '../helpers/utils';

/** Keep previous value of prop in ref for comparison between renders.
 * Wait until layer has been initializes before tracking value
 */
export function usePreviousLayer<T>(value: T, layer?: FeatureLayer | GraphicsLayer) {
    const ref = useRef<T>();
    useEffect(() => {
        if (layer) {
            ref.current = value;
        }
    });
    return ref.current;
}

type LayerControlProps<T> = {
    layer: FeatureLayer | undefined;
    currItems: T[];
    cacheKey?: string;
    /** Delete any existing layer features before adding new ones. Intended for
     * layers such as AML that should never contain more than one current feature.
     */
    enforceSingleFeatureInvariant?: boolean;
    mapMode?: MapMode;
    didItemChange?: (currItem: T, prevItem: T) => boolean;
    createGraphics: (items: T[]) => Graphic[];
    entityIdToObjectIdMapCacheRef: MutableRefObject<SimpleMemoryCache<number>>;
};

/** Add generic control for graphics on feature layer.
 * This hook manages the adding/deleting/updating of graphics on a layer
 * The currItems you pass to this hook requires you have the property entityId on each item to be able to uniquely identify each one
 * If you pass mapMode, all graphics will be updated if that value changes
 * didItemChange is a function that allows each layer to determine if an item needs updating outside the generic scope of being added or deleted
 * createGraphics is a function that takes in a number of items and returns esri graphic objects for each one
 * entityIdToObjectIdMapCacheRef is a map from entityId to ObjectId so we can identify each item to its graphic on the map
 */
export const useLayerControl = <T extends { entityId: number }>({
    layer,
    currItems,
    cacheKey,
    enforceSingleFeatureInvariant = false,
    mapMode,
    didItemChange,
    createGraphics,
    entityIdToObjectIdMapCacheRef,
}: LayerControlProps<T>) => {
    const prevItems = usePreviousLayer(currItems, layer);
    const prevMapMode = usePreviousLayer(mapMode, layer);

    useEffect(() => {
        if (layer) {
            const itemsToBeAdded: T[] = [];
            const itemsToBeUpdated: T[] = [];
            const itemsToBeDeleted: T[] = [];
            const dedupedCurrItems: T[] = [];
            const tempObj = new Map<number, T>();

            currItems.forEach((item) => {
                tempObj.set(item.entityId, item);
            });

            tempObj.forEach((item) => {
                if (item) {
                    dedupedCurrItems.push(item);
                }
            });

            const filteredPrevItems = prevItems?.filter((item) => !!item);
            // add all currItems if no prevItems
            if (!filteredPrevItems || !filteredPrevItems.length) {
                itemsToBeAdded.push(...dedupedCurrItems);
            }
            // delete all prevItems if no currItems
            else if (!dedupedCurrItems.length) {
                itemsToBeDeleted.push(...filteredPrevItems);
            }
            // update all currItems if the map mode changes
            else if (mapMode && prevMapMode !== mapMode) {
                itemsToBeUpdated.push(...dedupedCurrItems);
            }
            // case when prevItems and items exist
            else {
                // key by unit id for units and user id for users
                const prevItemsKeyedByEntityId = keyBy(filteredPrevItems, (item) => item.entityId);
                const currItemsKeyedByEntityId = keyBy(dedupedCurrItems, (item) => item.entityId);
                // iterate through currItems to figure out what needs to be added or updated
                for (let i = 0; i < dedupedCurrItems.length; i++) {
                    const currItem = dedupedCurrItems[i];
                    const entityId = currItem.entityId;
                    const prevItem = prevItemsKeyedByEntityId[entityId];
                    // if prevItem doesn't exist add unit
                    if (!prevItem) {
                        itemsToBeAdded.push(currItem);
                    } else if (didItemChange && didItemChange(currItem, prevItem)) {
                        itemsToBeUpdated.push(currItem);
                    }
                }
                // check which prevItems no longer exist in units, those are to be deleted
                for (let i = 0; i < filteredPrevItems.length; i++) {
                    const prevItem = filteredPrevItems[i];
                    if (!currItemsKeyedByEntityId[prevItem.entityId]) {
                        itemsToBeDeleted.push(prevItem);
                    }
                }
            }

            // build graphic of icons
            const itemGraphicsToBeAdded = createGraphics(itemsToBeAdded);
            const itemGraphicsToBeUpdated = createGraphics(itemsToBeUpdated);

            layer.queryFeatures().then((existingGraphics) => {
                const existingFeatureEntityIds = getFeatureEntityIds(existingGraphics.features);
                // compile edits into correct format
                const edits: FeatureLayerApplyEdits = {};
                // Find the existing graphics that we intend to delete and add those in an array which will be
                // assigned to `edits.deleteFeatures`. Fall back to `createGraphics` only if needed.
                const itemGraphicsToBeDeleted: Graphic[] = itemsToBeDeleted.map((item) => {
                    const graphic = existingGraphics.features.find((eg) => {
                        return (
                            (!!item.entityId && eg.attributes.entityId === item.entityId) ||
                            (!!eg.attributes.entityId &&
                                item.entityId &&
                                eg.attributes.entityId === item.entityId)
                        );
                    });
                    return graphic || createGraphics([item])[0];
                });

                if (itemGraphicsToBeAdded.length) {
                    edits.addFeatures = itemGraphicsToBeAdded;
                }
                if (itemGraphicsToBeUpdated.length) {
                    edits.updateFeatures = itemGraphicsToBeUpdated;
                }
                if (itemGraphicsToBeDeleted.length) {
                    edits.deleteFeatures = itemGraphicsToBeDeleted;
                }

                if (itemGraphicsToBeAdded.length > 0) {
                    existingGraphics.features.forEach((graphic) => {
                        const entityId = graphic.getAttribute('entityId');
                        const foundItem = itemsToBeAdded.find((item) => item.entityId === entityId);

                        if (foundItem) {
                            if (!edits.deleteFeatures) {
                                edits.deleteFeatures = [];
                            }

                            edits.deleteFeatures.push(graphic);
                        }
                    });
                }

                if (enforceSingleFeatureInvariant && itemGraphicsToBeAdded.length > 0) {
                    const plannedDeleteEntityIds = getFeatureEntityIds(edits.deleteFeatures ?? []);
                    const survivingExistingFeatureEntityIds = existingFeatureEntityIds.filter(
                        (entityId) => !plannedDeleteEntityIds.includes(entityId)
                    );

                    if (survivingExistingFeatureEntityIds.length > 0) {
                        logMapInfo({
                            source: 'layer',
                            event: 'aml_duplication_detected',
                            extraData: {
                                layerCacheKey: cacheKey,
                                existingFeatureEntityIds,
                                addEntityIds: itemsToBeAdded.map((item) => item.entityId),
                                deleteEntityIds: plannedDeleteEntityIds,
                                duplicateSignal: 'existing_feature_would_survive_new_add',
                            },
                        });
                    }

                    if (existingGraphics.features.length > 0) {
                        if (survivingExistingFeatureEntityIds.length > 0) {
                            logMapInfo({
                                source: 'layer',
                                event: 'aml_duplication_prevented',
                                extraData: {
                                    layerCacheKey: cacheKey,
                                    existingFeatureEntityIds,
                                    addEntityIds: itemsToBeAdded.map((item) => item.entityId),
                                    deleteEntityIds: getFeatureEntityIds(
                                        existingGraphics.features
                                    ),
                                    duplicateSignal:
                                        'existing_feature_removed_before_new_add',
                                },
                            });
                        }

                        edits.deleteFeatures = existingGraphics.features;
                    }
                }

                const hasEdits = !isEmpty(edits);

                // apply edits to the unit layer if there are any
                if (hasEdits) {
                    layer.applyEdits(edits).then((editsResult) => {
                        // Handle successful deletions - remove from cache
                        if (editsResult.deleteFeatureResults?.length) {
                            const cache = entityIdToObjectIdMapCacheRef.current;
                            const cacheEntries = cache.getAll();

                            const objectIdToEntityId = new Map<number, number>();
                            for (const [entityId, objectId] of Object.entries(cacheEntries)) {
                                objectIdToEntityId.set(objectId as number, Number(entityId));
                            }

                            editsResult.deleteFeatureResults.forEach((deleteResult) => {
                                if (!deleteResult.error && deleteResult.objectId) {
                                    const entityId = objectIdToEntityId.get(deleteResult.objectId);
                                    if (entityId !== undefined) {
                                        cache.delete(entityId);
                                    }
                                }
                            });
                        }

                        // Get the objectIds of the newly added features
                        const objectIds = map(editsResult.addFeatureResults, 'objectId');
                        // Add unitId -> objectId to the map so we can identify features
                        // for updating later
                        layer
                            .queryFeatures({
                                objectIds,
                            })
                            .then((results) => {
                                forEach(results.features, (item) => {
                                    entityIdToObjectIdMapCacheRef.current.put(
                                        item.attributes.entityId,
                                        item.attributes.objectId
                                    );
                                });
                            });
                    });
                }
            });
        }
    }, [
        currItems,
        prevItems,
        layer,
        cacheKey,
        enforceSingleFeatureInvariant,
        mapMode,
        prevMapMode,
        createGraphics,
        didItemChange,
        entityIdToObjectIdMapCacheRef,
    ]);
};
